import React, { PureComponent, ChangeEvent, CSSProperties } from 'react'
import { connect } from 'react-redux'
import AssessmentEditableHeader from './AssessmentEditableHeader'
import { Button } from '@lmc/ui-kit'
import SimilarAssessments from './SimilarAssessments'
import { mixpanel } from '../../../../common/services/analytics'
import { CarePlan } from '@lmc/ts-common'

type AssessmentItem = {
  id: string
  form?: {
    name: string
    iconText?: string
    color?: string
  }
  name?: string
  iconText?: string
  color?: string
  riskLevel?: string
  riskScore?: number
  incomplete?: boolean
  createdOn?: string
  reviewedOn?: string
  createdBy?: string
  carePlans?: Array<CarePlan>
}

type FormsAndAssessments = Array<AssessmentItem>

interface AssessmentTitleScreenProps {
  form: {
    name: string
  }
  onChange: (value: string) => void
  goToNextStep?: () => void
  style?: React.CSSProperties
  value?: string
  formsAndAssessments?: FormsAndAssessments
  carers?: Array<{
    id: string
    name: {
      first: string
      last: string
    }
  }>
  onDeleteSimilar?: (riskAssessmentId: string) => void
  carePlanNominative: string
  router?: {
    navigate: (path: string) => void
    params: { id?: string }
  }
  onClose?: () => void
  user?: {
    id: string
  }
  home?: {
    id: string
    name: string
  }
  addExisting: (riskAssessment: AssessmentItem, callback: () => void) => void
  addNew: (
    riskAssessmentId: string,
    isCustomTemplate: boolean,
    callback: () => void,
  ) => void
}

interface AssessmentTitleScreenState {
  hasChangedName: boolean
  filteredSimilarAssessments: Array<AssessmentItem>
  hasTrackedSimilarShown: boolean
}

export class AssessmentTitleScreen extends PureComponent<
  AssessmentTitleScreenProps,
  AssessmentTitleScreenState
> {
  state: AssessmentTitleScreenState = {
    hasChangedName: false,
    filteredSimilarAssessments: [],
    hasTrackedSimilarShown: false,
  }

  normalizeString = (str: string): string => {
    return str.toLowerCase().trim().replace(/\s+/g, ' ')
  }

  filterSimilarAssessments = (inputValue: string) => {
    const { formsAndAssessments = [] } = this.props

    if (inputValue.length < 3) {
      return []
    }

    const normalizedInput = this.normalizeString(inputValue)

    return formsAndAssessments.filter((assessment: AssessmentItem) => {
      const assessmentName = assessment.form?.name || assessment.name
      if (!assessmentName) return false

      const normalizedAssessmentName = this.normalizeString(assessmentName)
      return normalizedAssessmentName.includes(normalizedInput)
    })
  }

  handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (!this.state.hasChangedName) {
      this.setState({ hasChangedName: true })
    }

    const filteredSimilarAssessments = this.filterSimilarAssessments(value)

    this.setState({ filteredSimilarAssessments })
    this.props.onChange(value)
  }

  componentDidMount() {
    const { value } = this.props
    if (value) {
      const filteredSimilarAssessments = this.filterSimilarAssessments(value)
      this.setState({ filteredSimilarAssessments })
    }
  }

  componentDidUpdate(
    prevProps: AssessmentTitleScreenProps,
    prevState: AssessmentTitleScreenState,
  ) {
    const { filteredSimilarAssessments, hasTrackedSimilarShown } = this.state
    const { user, home, router, value } = this.props

    if (prevProps.value !== value && value) {
      const newFilteredAssessments = this.filterSimilarAssessments(value)
      this.setState({ filteredSimilarAssessments: newFilteredAssessments })
    }

    if (
      !hasTrackedSimilarShown &&
      prevState.filteredSimilarAssessments.length === 0 &&
      filteredSimilarAssessments.length > 0
    ) {
      const residentId = router?.params?.id

      mixpanel.track('similar_risk_assessments_shown', {
        platform: 'Care Office',
        distinct_id: user?.id,
        home: home?.id,
        homeName: home?.name,
        residentId,
        assessments_count: filteredSimilarAssessments.length,
      })

      this.setState({ hasTrackedSimilarShown: true })
    }

    if (filteredSimilarAssessments.length === 0 && hasTrackedSimilarShown) {
      this.setState({ hasTrackedSimilarShown: false })
    }
  }

  render() {
    const {
      form,
      goToNextStep,
      onChange,
      style,
      value,
      carers = [],
      onDeleteSimilar,
      carePlanNominative,
      router,
      user,
      home,
      addExisting,
      addNew,
    } = this.props
    const { filteredSimilarAssessments } = this.state

    if (!form || !onChange) return null

    const disabled = !this.state.hasChangedName || !value
    const showSimilarAssessments = filteredSimilarAssessments.length > 0

    return (
      <div style={style}>
        <div style={styles.titleContainer as CSSProperties}>{TITLE}</div>
        <div style={styles.contentContainer}>
          <AssessmentEditableHeader
            // @ts-ignore
            form={form}
            onChange={this.handleChange}
            value={value || ''}
          />
          <div style={styles.note}>{NOTE(form.name)}</div>
          {showSimilarAssessments && (
            <div style={styles.similarAssessmentsContainer}>
              <SimilarAssessments
                similarAssessments={filteredSimilarAssessments}
                carers={carers}
                onDelete={onDeleteSimilar}
                carePlanNominative={carePlanNominative}
                router={router}
                user={user}
                home={home}
                addExisting={addExisting}
                addNew={addNew}
                goToNextStep={goToNextStep}
              />
            </div>
          )}
          <div style={styles.buttonContainer}>
            <Button
              variant="primary"
              disabled={disabled}
              onClick={goToNextStep}
              style={styles.nextButton}
            >
              Create Custom
            </Button>
          </div>
        </div>
      </div>
    )
  }
}

const NOTE = (name: string) =>
  `By adding a custom title to a ${name}, you will be able to write an Assessment for any necessary Risk.`
const TITLE = 'Add a Custom Risk Assessment Title'

const styles = {
  button: {
    display: 'inline-block',
    float: 'right',
    marginTop: 24,
  },
  contentContainer: {
    marginTop: 64,
  },
  note: {
    marginTop: 24,
  },
  titleContainer: {
    fontWeight: 300,
    fontSize: 28,
    textAlign: 'center',
  },
  similarAssessmentsContainer: {
    marginTop: 24,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  nextButton: {},
}

const mapStateToProps = (state: {
  global: {
    user: { id: string }
    home: { id: string; name: string }
  }
}) => ({
  user: state.global.user,
  home: state.global.home,
})

export default connect(mapStateToProps)(AssessmentTitleScreen)
